/**
 * Asesoría — el programa para gestorías (`OD-0063` D-01, D-03 y D-04), desde
 * las dos puntas, en Configuración:
 *
 * ```text
 * TU ASESORIA    la empresa da acceso de lectura a su gestoria con el codigo
 *                que esta le da, ve el aviso permanente de quien puede ver sus
 *                datos (D-04 LO QUE VE EL CLIENTE) y lo retira en un clic
 * PROGRAMA PARA  si la empresa ES una gestoria: activa su perfil aceptando las
 * ASESORIAS      condiciones, ve su codigo, las empresas que le han dado acceso,
 *                y reparte cada una entre su equipo, hasta 3 personas
 * ```
 *
 * Solo la ven OWNER y ADMIN (`users.manage`), y solo con el programa encendido
 * (D-11 A): quien monta la pestaña ya lo ha comprobado. Lo que decide de verdad
 * es la API; aquí no se compara ningún plan ni ningún rol.
 *
 * Y una tarjeta para el panel, `InvitacionDeAsesoria`: a quien acaba de crear
 * su empresa por el enlace de una gestoría (D-08) se le PREGUNTA si le da
 * acceso, con dos botones y nada marcado de antemano (D-11).
 *
 * Y la invitación del CLIENTE a su gestoría (D-04, enmienda del 2026-10-28):
 *
 * ```text
 * INVITAR A MI     un boton: la API crea un enlace de un solo uso y se abre el
 * ASESORIA         correo del dispositivo con el texto escrito. La direccion la
 *                  pone el cliente en SU correo: aqui no se pregunta
 * AsesoriaQue-     en el Inicio del cliente: «X ya esta en Faktelio. ¿Le das
 * HaEntrado        acceso?», con un clic
 * InvitacionDe-    en el Inicio de la gestoria que entro por el enlace: de que
 * TuCliente        cliente era, y si le falta activar el perfil
 * ```
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  api,
  type AccesoDeAsesoria,
  type InvitacionAceptada,
  type InvitacionDeClienteAceptada,
  type ClienteDeAsesoria,
  type ClientesDeAsesoria,
  type EstadoPerfilAsesoria,
  type Resultado,
} from "../api/cliente";
import { asesoriaPendiente, olvidarAsesoria } from "../enlaces/asesoria-del-enlace";
import {
  descartarOferta,
  enlaceDeInvitacion,
  invitacionPendiente,
  mailtoDeInvitacion,
  ofertaDescartada,
  olvidarInvitacion,
} from "../enlaces/invitacion-de-cliente";
import { CAMINO } from "../enlaces/destino";
import { t } from "../i18n/es";
import { diaLegible } from "../ui/formato";
import { Aviso, Boton, Campo, Casilla, Selector, Tarjeta } from "../ui/primitivas";

const dia = (iso: string): string => diaLegible(iso.slice(0, 10));

/** Escrito como firma de llamada: `() => Promise<…>` lo lee como texto el guardrail de JSX. */
type AccionDelReparto = { (): Promise<Resultado<true>> };

export function PantallaAsesoria({ hayConexion }: { hayConexion: boolean }) {
  return (
    <>
      <TuAsesoria hayConexion={hayConexion} />
      <ProgramaParaAsesorias hayConexion={hayConexion} />
    </>
  );
}

/**
 * «Te ha traído X. ¿Le das acceso?» — sólo si se llegó por el enlace de una
 * gestoría (queda el código en el navegador), la empresa quedó atribuida y
 * todavía no ha dado acceso a nadie. Lo demás no pinta nada.
 *
 * El acceso se da con el código que dice la API, el de la gestoría que la
 * trajo, y no con el del navegador: otro enlace abierto después daría acceso a
 * otra. Contestar, sea lo que sea, olvida el código: no se vuelve a preguntar,
 * y el camino de siempre sigue en Configuración > Asesoría.
 */
export function InvitacionDeAsesoria({ hayConexion }: { hayConexion: boolean }) {
  const [delEnlace] = useState(() => asesoriaPendiente(Date.now()));
  const [oferta, setOferta] = useState<null | { readonly asesoria: string; readonly codigo: string; readonly incluido: boolean }>(null);
  const [hecho, setHecho] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    if (delEnlace === null) return;
    void Promise.all([api.atribucionAsesoria(), api.accesoAsesoria()]).then(([a, acceso]) => {
      // Un fallo de red no decide nada: se pregunta la proxima vez.
      if (!a.ok || !acceso.ok) return;
      const atribucion = a.datos.atribucion;
      if (atribucion === null || atribucion.codigo === null || acceso.datos.acceso !== null) {
        // Esta empresa no la trajo una gestoria con la que se pueda contar, o
        // ya tiene a la suya: el codigo no sirve aqui.
        olvidarAsesoria();
        return;
      }
      setOferta({
        asesoria: atribucion.asesoria ?? t.asesoria.invitacionTuAsesoria,
        codigo: atribucion.codigo,
        incluido: acceso.datos.incluidoEnElPlan,
      });
    });
  }, [delEnlace]);

  async function dar(): Promise<void> {
    if (oferta === null) return;
    setOcupado(true);
    setError(null);
    const r = await api.darAccesoAsesoria(oferta.codigo, false);
    setOcupado(false);
    if (!r.ok) {
      setError(r.error?.message ?? t.asesoria.error);
      return;
    }
    olvidarAsesoria();
    setHecho(true);
  }

  function ahoraNo(): void {
    olvidarAsesoria();
    setOferta(null);
  }

  if (hecho) return <Aviso tono="exito">{t.asesoria.accesoDado}</Aviso>;
  if (oferta === null) return null;
  return (
    <Tarjeta titulo={t.asesoria.invitacionTitulo(oferta.asesoria)}>
      <p>{t.asesoria.invitacionTexto(oferta.asesoria)}</p>
      {!oferta.incluido ? <Aviso tono="info">{t.asesoria.soloBusiness}</Aviso> : null}
      {error !== null ? <Aviso tono="error">{error}</Aviso> : null}
      <div className="acciones">
        {oferta.incluido ? (
          <Boton deshabilitado={!hayConexion || ocupado} onClick={() => void dar()}>
            {ocupado ? t.asesoria.dandoAcceso : t.asesoria.darAcceso}
          </Boton>
        ) : null}
        <Boton variante="terciario" deshabilitado={ocupado} onClick={ahoraNo}>
          {t.asesoria.ahoraNo}
        </Boton>
      </div>
    </Tarjeta>
  );
}

/** El lado del cliente: dar, ver y retirar el acceso. */
function TuAsesoria({ hayConexion }: { hayConexion: boolean }) {
  const [estado, setEstado] = useState<AccesoDeAsesoria | null>(null);
  const [codigo, setCodigo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [errorCodigo, setErrorCodigo] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  /** D-04: dar acceso a otra gestoría avisa ANTES de quitárselo a la anterior. */
  const [sustituirA, setSustituirA] = useState<string | null>(null);
  const [confirmarRetirar, setConfirmarRetirar] = useState(false);

  const cargar = useCallback(async () => {
    const r = await api.accesoAsesoria();
    if (r.ok) setEstado(r.datos);
    else setError(r.error?.message ?? t.asesoria.error);
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function dar(sustituir: boolean): Promise<void> {
    setOcupado(true);
    setError(null);
    setErrorCodigo(null);
    const r = await api.darAccesoAsesoria(codigo, sustituir);
    setOcupado(false);
    if (r.ok) {
      setCodigo("");
      setSustituirA(null);
      await cargar();
      return;
    }
    if (r.error?.motivo === "ADVISOR_ALREADY_GRANTED") {
      const anterior = r.error.detalles.asesoria;
      setSustituirA(typeof anterior === "string" ? anterior : "");
      return;
    }
    if (r.error?.campos.codigo !== undefined || r.estado === 404) setErrorCodigo(r.error?.message ?? t.asesoria.error);
    else setError(r.error?.message ?? t.asesoria.error);
  }

  async function retirar(): Promise<void> {
    setOcupado(true);
    setError(null);
    const r = await api.retirarAccesoAsesoria();
    setOcupado(false);
    setConfirmarRetirar(false);
    if (!r.ok) setError(r.error?.message ?? t.asesoria.error);
    await cargar();
  }

  function enviar(e: FormEvent): void {
    e.preventDefault();
    void dar(false);
  }

  if (estado === null) return error !== null ? <Aviso tono="error">{error}</Aviso> : null;

  return (
    <Tarjeta titulo={t.asesoria.tuAsesoria}>
      {error !== null ? <Aviso tono="error">{error}</Aviso> : null}
      {estado.acceso !== null ? (
        <>
          <Aviso tono="info" titulo={estado.acceso.asesoria}>
            {t.asesoria.avisoPermanente(estado.acceso.asesoria ?? "")} {t.asesoria.desde(dia(estado.acceso.desde))}
          </Aviso>
          <h3 className="subtitulo">{t.asesoria.personas}</h3>
          {estado.acceso.personas.length === 0 ? (
            <p className="ayuda-neutra">{t.asesoria.sinPersonas}</p>
          ) : (
            <ul>
              {estado.acceso.personas.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          )}
          {confirmarRetirar ? (
            <Aviso tono="aviso">{t.asesoria.confirmarRetirar}</Aviso>
          ) : null}
          <div className="acciones">
            <Boton
              variante="peligro"
              deshabilitado={!hayConexion || ocupado}
              onClick={() => (confirmarRetirar ? void retirar() : setConfirmarRetirar(true))}
            >
              {ocupado ? t.asesoria.retirando : t.asesoria.retirar}
            </Boton>
            {confirmarRetirar ? (
              <Boton variante="terciario" onClick={() => setConfirmarRetirar(false)}>
                {t.asesoria.cancelar}
              </Boton>
            ) : null}
          </div>
        </>
      ) : (
        <p>{t.asesoria.explicacion}</p>
      )}
      {estado.acceso === null && !estado.incluidoEnElPlan ? (
        <Aviso tono="info">{t.asesoria.soloBusiness}</Aviso>
      ) : null}
      {estado.acceso === null && estado.incluidoEnElPlan
        ? estado.invitaciones.map((i) => (
            <AsesoriaInvitada key={i.invitacion} invitada={i} hayConexion={hayConexion} onHecho={() => void cargar()} />
          ))
        : null}
      {estado.incluidoEnElPlan && (estado.acceso === null || sustituirA !== null) ? (
        <form onSubmit={enviar} noValidate>
          <Campo
            id="codigo-asesoria"
            etiqueta={t.asesoria.codigo}
            valor={codigo}
            onChange={(v) => {
              setCodigo(v);
              setSustituirA(null);
            }}
            ayuda={t.asesoria.codigoAyuda}
            error={errorCodigo}
            autoComplete="off"
          />
          {sustituirA !== null ? (
            <>
              <Aviso tono="aviso" titulo={t.asesoria.sustituirTitulo}>
                {t.asesoria.sustituirTexto(sustituirA)}
              </Aviso>
              <Boton variante="peligro" deshabilitado={!hayConexion || ocupado} onClick={() => void dar(true)}>
                {t.asesoria.sustituir}
              </Boton>
            </>
          ) : (
            <Boton tipo="submit" deshabilitado={!hayConexion || ocupado || codigo.trim() === ""}>
              {ocupado ? t.asesoria.dandoAcceso : t.asesoria.darAcceso}
            </Boton>
          )}
        </form>
      ) : null}
      {estado.incluidoEnElPlan && estado.acceso === null ? <InvitarMiAsesoria hayConexion={hayConexion} /> : null}
    </Tarjeta>
  );
}

/**
 * «Invitar a mi asesoría» (D-04, enmienda del 2026-10-28): UN botón. Pide el
 * enlace a la API y abre el correo del dispositivo —Gmail, Outlook, el del
 * móvil, el que tenga— con el asunto y el texto escritos. Si no se abre nada,
 * el enlace queda a la vista para copiarlo.
 */
function InvitarMiAsesoria({ hayConexion }: { hayConexion: boolean }) {
  const [enlace, setEnlace] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  async function invitar(): Promise<void> {
    setOcupado(true);
    setError(null);
    const r = await api.invitarAMiAsesoria();
    setOcupado(false);
    if (!r.ok) {
      setError(r.error?.message ?? t.asesoria.error);
      return;
    }
    const url = enlaceDeInvitacion(window.location.origin, CAMINO.registro, r.datos.token);
    const empresa = r.datos.empresa ?? t.asesoria.tuEmpresa;
    // Los dias salen de la caducidad que dice la API, no de una copia de su constante.
    const dias = Math.max(1, Math.round((Date.parse(r.datos.caduca) - Date.now()) / 86_400_000));
    setEnlace(url);
    window.location.href = mailtoDeInvitacion(t.asesoria.correoAsunto(empresa), t.asesoria.correoCuerpo(empresa, url, dias));
  }

  return (
    <>
      <h3 className="subtitulo">{t.asesoria.invitarMiAsesoria}</h3>
      <p className="ayuda-neutra">{t.asesoria.invitarMiAsesoriaAyuda}</p>
      {error !== null ? <Aviso tono="error">{error}</Aviso> : null}
      <Boton variante="secundario" deshabilitado={!hayConexion || ocupado} onClick={() => void invitar()}>
        {ocupado ? t.asesoria.abriendoCorreo : t.asesoria.invitarMiAsesoria}
      </Boton>
      {enlace !== null ? (
        <>
          <p className="ayuda-neutra">{t.asesoria.siNoSeAbre}</p>
          <p className="enlace-copiable">
            <code>{enlace}</code>
          </p>
        </>
      ) : null}
    </>
  );
}

/**
 * Una gestoría que entró por la invitación del cliente: su nombre y el clic
 * que le da acceso. El acceso lo da la API con el id de ESA invitación, no con
 * un código: la API comprueba que es de esta empresa y que la gestoría sigue
 * activa.
 */
function AsesoriaInvitada({
  invitada,
  hayConexion,
  onHecho,
  onAhoraNo,
}: {
  invitada: InvitacionAceptada;
  hayConexion: boolean;
  onHecho: () => void;
  onAhoraNo?: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const nombre = invitada.asesoria ?? t.asesoria.invitacionTuAsesoria;

  async function dar(): Promise<void> {
    setOcupado(true);
    setError(null);
    const r = await api.darAccesoPorInvitacion(invitada.invitacion);
    setOcupado(false);
    if (!r.ok) {
      setError(r.error?.message ?? t.asesoria.error);
      return;
    }
    onHecho();
  }

  return (
    <Aviso tono="info" titulo={t.asesoria.yaEsta(nombre)}>
      <p>{t.asesoria.yaEstaTexto(nombre)}</p>
      {error !== null ? <Aviso tono="error">{error}</Aviso> : null}
      <div className="acciones">
        <Boton deshabilitado={!hayConexion || ocupado} onClick={() => void dar()}>
          {ocupado ? t.asesoria.dandoAcceso : t.asesoria.darAcceso}
        </Boton>
        {onAhoraNo !== undefined ? (
          <Boton variante="terciario" deshabilitado={ocupado} onClick={onAhoraNo}>
            {t.asesoria.ahoraNo}
          </Boton>
        ) : null}
      </div>
    </Aviso>
  );
}

/**
 * En el Inicio del CLIENTE: la gestoría que invitó ya está en Faktelio. Sale
 * si la empresa no ha dado acceso a nadie y está en Business. «Ahora no» la
 * esconde en ESTE navegador; en Configuración > Asesoría sigue estando.
 */
export function AsesoriaQueHaEntrado({ hayConexion }: { hayConexion: boolean }) {
  const [oferta, setOferta] = useState<InvitacionAceptada | null>(null);
  const [hecho, setHecho] = useState(false);

  useEffect(() => {
    void api.accesoAsesoria().then((r) => {
      if (!r.ok || r.datos.acceso !== null || !r.datos.incluidoEnElPlan) return;
      setOferta(r.datos.invitaciones.find((i) => !ofertaDescartada(i.invitacion)) ?? null);
    });
  }, []);

  if (hecho) return <Aviso tono="exito">{t.asesoria.accesoDado}</Aviso>;
  if (oferta === null) return null;
  return (
    <AsesoriaInvitada
      invitada={oferta}
      hayConexion={hayConexion}
      onHecho={() => setHecho(true)}
      onAhoraNo={() => {
        descartarOferta(oferta.invitacion);
        setOferta(null);
      }}
    />
  );
}

/**
 * En el Inicio de la GESTORIA que abrió el enlace de su cliente: se manda el
 * token a la API en cuanto hay empresa, y se olvida con la respuesta, sea la
 * que sea. Sin red no se olvida: se prueba la próxima vez.
 */
export function InvitacionDeTuCliente({ onIrAAsesoria }: { onIrAAsesoria: () => void }) {
  const [token] = useState(() => invitacionPendiente(Date.now()));
  const [aceptada, setAceptada] = useState<InvitacionDeClienteAceptada | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cerrada, setCerrada] = useState(false);

  useEffect(() => {
    if (token === null) return;
    void api.aceptarInvitacionDeCliente(token).then((r) => {
      if (!r.ok && r.estado === 0) return;
      olvidarInvitacion();
      if (r.ok) setAceptada(r.datos);
      else setError(r.error?.message ?? t.asesoria.error);
    });
  }, [token]);

  if (cerrada) return null;
  if (error !== null) return <Aviso tono="aviso">{error}</Aviso>;
  if (aceptada === null) return null;
  return (
    <Tarjeta titulo={t.asesoria.teHaInvitado(aceptada.cliente ?? t.asesoria.unCliente)}>
      <p>{aceptada.perfilActivo ? t.asesoria.teHaInvitadoListo : t.asesoria.teHaInvitadoActiva}</p>
      <div className="acciones">
        {aceptada.perfilActivo ? null : <Boton onClick={onIrAAsesoria}>{t.asesoria.activar}</Boton>}
        <Boton variante="terciario" onClick={() => setCerrada(true)}>
          {t.asesoria.entendido}
        </Boton>
      </div>
    </Tarjeta>
  );
}

/** El lado de la gestoría: su perfil, su código y el reparto. */
function ProgramaParaAsesorias({ hayConexion }: { hayConexion: boolean }) {
  const [perfil, setPerfil] = useState<EstadoPerfilAsesoria | null>(null);
  const [lista, setLista] = useState<ClientesDeAsesoria | null>(null);
  const [acepta, setAcepta] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const cargar = useCallback(async () => {
    const p = await api.perfilAsesoria();
    if (!p.ok) {
      setError(p.error?.message ?? t.asesoria.error);
      return;
    }
    setPerfil(p.datos);
    if (p.datos.perfil !== null) {
      const l = await api.clientesAsesoria();
      if (l.ok) setLista(l.datos);
      else setError(l.error?.message ?? t.asesoria.error);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function activar(): Promise<void> {
    if (perfil === null) return;
    setOcupado(true);
    setError(null);
    const r = await api.activarPerfilAsesoria(perfil.condiciones);
    setOcupado(false);
    if (!r.ok) setError(r.error?.message ?? t.asesoria.error);
    await cargar();
  }

  /** Cualquier acción del reparto: la hace, enseña su error si lo hay, y relee. */
  async function hacer(accion: AccionDelReparto) {
    setOcupado(true);
    setError(null);
    const r = await accion();
    setOcupado(false);
    if (!r.ok) setError(r.error?.message ?? t.asesoria.error);
    await cargar();
  }

  if (perfil === null) return error !== null ? <Aviso tono="error">{error}</Aviso> : null;

  return (
    <Tarjeta titulo={t.asesoria.programa}>
      {error !== null ? <Aviso tono="error">{error}</Aviso> : null}
      {/* Suspendida no recibe acceso (D-01): ahi no se le dice que lo espere. */}
      {perfil.invitadaPor.length > 0 && perfil.perfil?.estado !== "SUSPENDED" ? (
        <Aviso tono="info">
          {perfil.perfil === null
            ? t.asesoria.teEsperan(perfil.invitadaPor.join(", "))
            : t.asesoria.teEsperanActivo(perfil.invitadaPor.join(", "))}
        </Aviso>
      ) : null}
      {perfil.perfil === null ? (
        <>
          <p>{t.asesoria.programaExplicacion}</p>
          <Casilla id="acepta-condiciones" etiqueta={t.asesoria.aceptoCondiciones} marcada={acepta} onChange={setAcepta} />
          <p className="ayuda-neutra">{t.asesoria.condicionesBorrador(perfil.condiciones)}</p>
          <Boton deshabilitado={!hayConexion || ocupado || !acepta} onClick={() => void activar()}>
            {ocupado ? t.asesoria.activando : t.asesoria.activar}
          </Boton>
        </>
      ) : (
        <>
          {perfil.perfil.estado === "SUSPENDED" ? <Aviso tono="aviso">{t.asesoria.suspendido}</Aviso> : null}
          <dl className="datos">
            <dt>{t.asesoria.tuCodigo}</dt>
            <dd>
              <code>{perfil.perfil.codigo}</code>
            </dd>
          </dl>
          <p className="ayuda-neutra">{t.asesoria.tuCodigoAyuda}</p>
          {perfil.perfil.estado === "ACTIVE" ? (
            <dl className="datos">
              <dt>{t.asesoria.tuEnlace}</dt>
              <dd>
                <code>{enlaceDeAlta(perfil.perfil.codigo)}</code>
              </dd>
            </dl>
          ) : null}
          {perfil.perfil.estado === "ACTIVE" ? <p className="ayuda-neutra">{t.asesoria.tuEnlaceAyuda}</p> : null}
          {perfil.perfil.estado === "ACTIVE" ? <InvitarCliente hayConexion={hayConexion} /> : null}
          <h3 className="subtitulo">{t.asesoria.clientes}</h3>
          {lista === null || lista.clientes.length === 0 ? (
            <p className="ayuda-neutra">{t.asesoria.sinClientes}</p>
          ) : (
            <ul className="lista-asesoria">
              {lista.clientes.map((c) => (
                <FilaCliente
                  key={c.organizationId}
                  cliente={c}
                  equipo={lista.equipo}
                  ocupado={!hayConexion || ocupado}
                  // D-01: suspendido no reparte; quitar y renunciar, siempre.
                  puedeRepartir={perfil.perfil?.estado === "ACTIVE"}
                  onAsignar={(userId) => void hacer(() => api.asignarPersonaAsesoria(c.organizationId, userId))}
                  onQuitar={(userId) => void hacer(() => api.quitarPersonaAsesoria(c.organizationId, userId))}
                  onRenunciar={() => void hacer(() => api.renunciarClienteAsesoria(c.organizationId))}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </Tarjeta>
  );
}

/** D-08 INVITAR DESDE EL PANEL: el correo de un cliente, y se le manda la invitación. */
function InvitarCliente({ hayConexion }: { hayConexion: boolean }) {
  const [correo, setCorreo] = useState("");
  const [hecho, setHecho] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorCorreo, setErrorCorreo] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  async function enviar(e: FormEvent): Promise<void> {
    e.preventDefault();
    setOcupado(true);
    setHecho(null);
    setError(null);
    setErrorCorreo(null);
    const destino = correo.trim();
    const r = await api.invitarClienteAsesoria(destino);
    setOcupado(false);
    if (r.ok) {
      setCorreo("");
      setHecho(r.datos === "INVITACION" ? t.asesoria.invitacionEnviada(destino) : t.asesoria.recordatorioEnviado(destino));
      return;
    }
    if (r.error?.campos.correo !== undefined) setErrorCorreo(r.error.message);
    else setError(r.error?.message ?? t.asesoria.error);
  }

  return (
    <form onSubmit={(e) => void enviar(e)} noValidate>
      <h3 className="subtitulo">{t.asesoria.invitar}</h3>
      <p className="ayuda-neutra">{t.asesoria.invitarAyuda}</p>
      <Campo
        id="invitar-correo"
        etiqueta={t.asesoria.correoCliente}
        valor={correo}
        onChange={setCorreo}
        error={errorCorreo}
        autoComplete="off"
      />
      {hecho !== null ? <Aviso tono="exito">{hecho}</Aviso> : null}
      {error !== null ? <Aviso tono="error">{error}</Aviso> : null}
      <Boton tipo="submit" variante="secundario" deshabilitado={!hayConexion || ocupado || correo.trim() === ""}>
        {ocupado ? t.asesoria.enviandoInvitacion : t.asesoria.enviarInvitacion}
      </Boton>
    </form>
  );
}

/**
 * D-08: el enlace que reparte la gestoría. Va a la aplicación y no a una página
 * del sitio público, que no tiene servidor que lea el código.
 */
function enlaceDeAlta(codigo: string): string {
  return `${window.location.origin}${CAMINO.registro}?asesoria=${encodeURIComponent(codigo)}`;
}

const MAX_PERSONAS = 3;

function FilaCliente({
  cliente,
  equipo,
  ocupado,
  puedeRepartir,
  onAsignar,
  onQuitar,
  onRenunciar,
}: {
  cliente: ClienteDeAsesoria;
  equipo: ClientesDeAsesoria["equipo"];
  ocupado: boolean;
  puedeRepartir: boolean;
  onAsignar: (userId: string) => void;
  onQuitar: (userId: string) => void;
  onRenunciar: () => void;
}) {
  const [elegido, setElegido] = useState("");
  const [confirmar, setConfirmar] = useState(false);
  const nombre = cliente.nombre ?? cliente.organizationId;
  const libres = equipo.filter((p) => !cliente.personas.some((x) => x.userId === p.userId));
  const lleno = cliente.personas.length >= MAX_PERSONAS;
  const deshabilitado = ocupado;

  return (
    <li className="fila-asesoria">
      <strong>{nombre}</strong>
      {!cliente.enBusiness ? (
        // D-03 PLANES: sin Business, la fila sale en gris con el motivo y sin nada más.
        <p className="ayuda-neutra">{t.asesoria.sinBusiness}</p>
      ) : (
        <>
          <p>
            {t.asesoria.llevan}:{" "}
            {cliente.personas.length === 0 ? t.asesoria.nadie : cliente.personas.map((p) => p.email ?? p.userId).join(", ")}
          </p>
          {cliente.personas.map((p) => (
            <Boton key={p.userId} variante="terciario" deshabilitado={deshabilitado} onClick={() => onQuitar(p.userId)}>
              {t.asesoria.quitar(p.email ?? p.userId)}
            </Boton>
          ))}
          {lleno ? (
            <p className="ayuda-neutra">{t.asesoria.limite}</p>
          ) : libres.length > 0 && puedeRepartir ? (
            <div className="acciones">
              <Selector
                id={`anadir-${cliente.organizationId}`}
                etiqueta={t.asesoria.anadirA(nombre)}
                valor={elegido}
                opciones={[{ valor: "", texto: t.asesoria.elegir }, ...libres.map((p) => ({ valor: p.userId, texto: p.email ?? p.userId }))]}
                onChange={setElegido}
                deshabilitado={deshabilitado}
              />
              <Boton
                variante="secundario"
                deshabilitado={deshabilitado || elegido === ""}
                onClick={() => {
                  onAsignar(elegido);
                  setElegido("");
                }}
              >
                {t.asesoria.anadir}
              </Boton>
            </div>
          ) : null}
        </>
      )}
      {confirmar ? <Aviso tono="aviso">{t.asesoria.confirmarRenunciar(nombre)}</Aviso> : null}
      <div className="acciones">
        <Boton variante="peligro" deshabilitado={deshabilitado} onClick={() => (confirmar ? onRenunciar() : setConfirmar(true))}>
          {t.asesoria.renunciar}
        </Boton>
        {confirmar ? (
          <Boton variante="terciario" onClick={() => setConfirmar(false)}>
            {t.asesoria.cancelar}
          </Boton>
        ) : null}
      </div>
    </li>
  );
}
